import PropTypes from 'prop-types'

import { i18nMessage } from './propTypes'

// Helper function to create prop-types for forms
//
// This is needed to avoid repetition of props like `values`, `errors` and
// `touched` of Formik
//
// This introduces a small DSL where complex proptypes like `PropTypes.shape`
// and `PropTypes.arrayOf` have to be specified differently.
//
// This is needed because we need to access the inner values of these complex
// proptypes to generate the nested errors/touched values.
//
// Note that directly using `PropTypes.shape` or `PropTypes.arrayOf` is still
// useful, but only if you want to treat them as opaque objects. I.e., their
// `touched` and `errors` are not nested and, instead, are just a simple literal
// value.
//
// How to specify proptypes:
//
// - Simple proptypes: just use the normal `PropTypes` package. E.g.:
//   `PropTypes.string`
//
// - `shape` proptypes: instead of using `PropTypes.shape` you have to use
//
//   {
//     type: 'shape',
//     isRequired: true, // this is optional
//     shape: {
//       ...
//       // This is an object of `key` to `propType`.
//       // It can have nested complex proptypes
//     }
//   }
//
// - `arrayOf` proptypes: instead of using `PropTypes.arrayOf` you have to use
//
//   {
//     type: 'arrayOf',
//     isRequired: true, // this is optional
//     of: ... // this is a `propType`, either a regular proptype or one of our
//             // complex proptypes for further nesting
//   }
//
// For examples see `src/common/prop-types.js`
export const makeFormPropTypes = fieldPropTypes => ({
  values: makeValuesPropType(fieldPropTypes).isRequired,

  touched: makeTouchedPropType({
    type: 'shape',
    shape: fieldPropTypes,
  }).isRequired,

  fieldErrors: makeFieldErrorsPropType(fieldPropTypes).isRequired,

  formErrors: formErrorsPropType.isRequired,

  onChange: PropTypes.func.isRequired,

  onBlur: PropTypes.func.isRequired,
})

export const makeValuesPropType = fieldPropTypes =>
  makeValuePropType({
    type: 'shape',
    shape: fieldPropTypes,
  })

export const makeFieldErrorsPropType = fieldPropTypes =>
  makeErrorPropType({
    type: 'shape',
    shape: fieldPropTypes,
  })

const makeValuePropType = propType => {
  // Base case: a normal proptype
  if (!propType.type) {
    return propType
  }

  // Our custom DSL
  switch (propType.type) {
    case 'shape': {
      let mapped = PropTypes.shape(
        Object.fromEntries(
          Object.entries(propType.shape).map(([field, propType]) => [
            field,
            makeValuePropType(propType),
          ]),
        ),
      )

      if (propType.isRequired) {
        mapped = mapped.isRequired
      }

      return mapped
    }

    case 'arrayOf': {
      let mapped = PropTypes.arrayOf(makeValuePropType(propType.of))

      if (propType.isRequired) {
        mapped = mapped.isRequired
      }

      return mapped
    }

    default: {
      throw new Error('Unknown custom propType')
    }
  }
}

const makeErrorPropType = propType => {
  // Base case: a normal proptype
  if (!propType.type) {
    return i18nMessage
  }

  // Our custom DSL
  switch (propType.type) {
    case 'shape': {
      return PropTypes.shape(
        Object.fromEntries(
          Object.entries(propType.shape).map(([field, propType]) => [
            field,
            makeErrorPropType(propType),
          ]),
        ),
      )
    }

    case 'arrayOf': {
      return PropTypes.arrayOf(makeErrorPropType(propType.of))
    }

    default: {
      throw new Error('Unknown custom propType')
    }
  }
}

const makeTouchedPropType = propType => {
  // Base case: a normal proptype
  if (!propType.type) {
    return PropTypes.bool
  }

  // Our custom DSL
  switch (propType.type) {
    case 'shape': {
      return PropTypes.shape(
        Object.fromEntries(
          Object.entries(propType.shape).map(([field, propType]) => [
            field,
            makeTouchedPropType(propType),
          ]),
        ),
      )
    }

    case 'arrayOf': {
      return PropTypes.arrayOf(makeTouchedPropType(propType.of))
    }

    default: {
      throw new Error('Unknown custom propType')
    }
  }
}

export const formErrorsPropType = PropTypes.arrayOf(i18nMessage)
